import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { rsimGraphPeriodTypeAtom } from 'src/atoms/filters/rsim-graphs';
import {
  PeriodTypeSwitch,
  PeriodTypeValue,
} from 'src/common/components/PeriodTypeSwitch/PeriodTypeSwitch';
import { Widget } from 'src/dashboard/Componets/Widget';
import { GraphsColorLegend } from 'src/common/components/GraphColorsLegend/GraphColorsLegend';
import { AreaChartWithUserSelectAndSuspenseContainer } from 'src/common/components/Graphs/common/Graphs/AreaChartWithSelectSuspend';
import { AreaChartErrorBoundary } from 'src/dashboard/Componets/AreaErrorBoundary';
import { PercentageAreaChart } from 'src/common/components/Graphs/common/Graphs/PercentageAreaChart';
import {
  BLUE_AREA_COLOR,
  GREEN_AREA_COLOR,
  RED_AREA_COLOR,
  useRSimPerformanceData,
} from './hooks/useRSimPerformanceData';
import { accountsFilterAtom } from 'src/atoms/filters/rsim-products';
import { GraphAccountsValidator } from 'src/products/GraphAccountValidator';
import { useAccountRefs } from 'src/common/components/Accounts/useAccountRefs';
import { PerformanceChartPlaceholder } from 'src/products/GraphValidatorPlaceholders';

const StyledChartContainer = styled(Box)(() => ({
  height: '100%',
}));

const getEmptyChartLabels = (numberOfLabels = 10) => {
  const now = new Date();

  const rounded = new Date(now);
  rounded.setMinutes(now.getMinutes() < 30 ? 0 : 30, 0, 0);

  const timestamps = [];

  for (let i = numberOfLabels - 1; i >= 0; i--) {
    const date = new Date(rounded.getTime() - i * 30 * 60 * 1000);
    const formatted =
      String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0');
    timestamps.push(formatted);
  }

  return timestamps;
};

export const RSimPerformanceChartWidget = () => {
  const { t } = useTranslation();

  const accountRefs = useAccountRefs({ cslAccountAtom: accountsFilterAtom });
  const [periodType, setPeriodType] = useAtom(rsimGraphPeriodTypeAtom);

  const shouldWidgetHeaderContent = accountRefs.length === 1;

  return (
    <GraphAccountsValidator
      accountRefs={accountRefs}
      fallback={<PerformanceChartPlaceholder timeXAxisLabels={getEmptyChartLabels()} />}
    >
      <Widget
        title={t('rSimInventory.rsimPerformance')}
        headerContent={
          shouldWidgetHeaderContent ? (
            <>
              <PeriodTypeSwitch value={periodType} onChange={setPeriodType} />

              <Box sx={{ marginLeft: 'auto' }}>
                <GraphsColorLegend
                  legendItems={[
                    { color: GREEN_AREA_COLOR, description: 'Primary' },
                    { color: BLUE_AREA_COLOR, description: 'Backup' },
                    { color: RED_AREA_COLOR, description: 'Disconnected' },
                  ]}
                  asTooltip
                />
              </Box>
            </>
          ) : undefined
        }
      >
        <AreaChartErrorBoundary key={`${accountRefs[0]} ${periodType}`}>
          <AreaChartWithUserSelectAndSuspenseContainer
            accountRef={accountRefs[0]}
            xAxisLabels={getEmptyChartLabels()}
          >
            {(accountRef) => <PerformanceChart accountRef={accountRef} periodType={periodType} />}
          </AreaChartWithUserSelectAndSuspenseContainer>
        </AreaChartErrorBoundary>
      </Widget>
    </GraphAccountsValidator>
  );
};

type PerformanceChartProps = {
  accountRef: string;
  periodType: PeriodTypeValue;
};

const PerformanceChart = ({ accountRef, periodType }: PerformanceChartProps) => {
  const { t } = useTranslation();
  const performanceData = useRSimPerformanceData({ accountRef, periodType });
  const isEmptyDataset = performanceData?.datasets.every((set) => set.data.length === 0);

  const TOOLTIP_ITEM_PRIORITY = {
    [t('graphs.performanceChartLabels.primary')]: 0,
    [t('graphs.performanceChartLabels.backup')]: 1,
    [t('graphs.performanceChartLabels.disconnected')]: 2,
  };

  return (
    <StyledChartContainer role="figure" aria-label="Performance">
      <PercentageAreaChart
        datasets={performanceData.datasets}
        xAxisLabels={isEmptyDataset ? getEmptyChartLabels() : performanceData.xAxisLabels}
        tooltipItemPriority={TOOLTIP_ITEM_PRIORITY}
      />
    </StyledChartContainer>
  );
};
